import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import { useLocation } from "react-router-dom";
import "react-phone-input-2/lib/style.css";
import "../assets/css/Contact.css";

import { API_URL } from "../config/constants";
import { trackEvent } from "../utils/analytics";
import contactPic from "../assets/images/contact-pic.jpeg";
import careersBg from "../assets/images/share-cv-bg.jpg";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];

const Contact = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    enquiryType: null,
    message: "",
    cv: null
  });

  const [submitting, setSubmitting] = useState(false);

  const enquiryOptions = [
    { value: "Buying Car", label: t("contact.enquiry.buying") },
    { value: "Selling Support", label: t("contact.enquiry.selling") },
    { value: "Dealer Partnership", label: t("contact.enquiry.dealer") },
    { value: "Careers", label: t("contact.enquiry.career") },
    { value: "Other", label: t("contact.enquiry.other") }
  ];

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash !== "#career") return;

      setForm((prev) => {
        // ⛔ Prevent infinite loop
        if (prev.enquiryType?.value === "Careers") return prev;

        return {
          ...prev,
          enquiryType: {
            value: "Careers",
            label: t("contact.enquiry.career")
          }
        };
      });

      document
        .getElementById("contact-form")
        ?.scrollIntoView({ behavior: "smooth" });
    };

    // Run once on initial load
    handleHashChange();

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [t]); // ✅ stable dependency

  useEffect(() => {
    const carName = location.state?.carName;
    const shouldPrefillBuying = location.state?.prefillBuying;
    if (!carName || !shouldPrefillBuying) return;

    setForm((prev) => ({
      ...prev,
      enquiryType: {
        value: "Buying Car",
        label: t("contact.enquiry.buying")
      },
      message: `I am interested in ${carName}, Kindly Revert to me with details and a callback`
    }));

    document
      .getElementById("contact-form")
      ?.scrollIntoView({ behavior: "smooth" });
  }, [location.state, t]);


  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (selectedOption) => {
    setForm({ ...form, enquiryType: selectedOption, cv: null });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(t("contact.errors.invalidCvType"));
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t("contact.errors.cvTooLarge"));
      e.target.value = "";
      return;
    }

    setForm({ ...form, cv: file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = form.name.trim();
    const phone = form.phone.replace(/\s+/g, "");
    const email = form.email.trim();
    const enquiryType = form.enquiryType?.value;
    const message = form.message.trim();

    const nameRegex = /^[a-zA-Z\s]{2,}$/;
    const phoneRegex = /^[0-9]{8,15}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (!name || !phone || !email || !enquiryType || !message) {
      toast.error(t("contact.errors.fillAll"));
      return;
    }

    if (!nameRegex.test(name)) {
      toast.error(t("contact.errors.invalidName"));
      return;
    }

    if (!phoneRegex.test(phone)) {
      toast.error(t("contact.errors.invalidPhone"));
      return;
    }

    if (!emailRegex.test(email)) {
      toast.error(t("contact.errors.invalidEmail"));
      return;
    }

    if (message.length < 10 || message.length > 500) {
      toast.error(t("contact.errors.invalidMessageLength"));
      return;
    }

    if (enquiryType === "Careers" && !form.cv) {
      toast.error(t("contact.errors.cvRequired"));
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading(t("contact.messages.submitting"));

    try {
      let response;

      if (form.cv) {
        const formData = new FormData();
        formData.append("name", name);
        formData.append("phone", phone);
        formData.append("email", email);
        formData.append("enquiryType", enquiryType);
        formData.append("message", message);
        formData.append("cv", form.cv);

        response = await fetch(`${API_URL}/leads`, {
          method: "POST",
          body: formData
        });
      } else {
        response = await fetch(`${API_URL}/leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            phone,
            email,
            enquiryType,
            message
          })
        });
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      trackEvent("generate_lead", {
        enquiry_type: enquiryType
      });

      toast.success(t("contact.messages.success"), { id: toastId });

      setForm({
        name: "",
        phone: "",
        email: "",
        enquiryType: null,
        message: "",
        cv: null
      });
    } catch (err) {
      toast.error(err.message || t("contact.errors.failed"), {
        id: toastId
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="contact">
      <section className="contact-hero">
        <div className="container">
          <h2 className="text-center mb-5">{t("contact.heading")}</h2>

          <div className="row align-items-center g-5 mt-4 new-container">
            <div className="col-md-1 text-center"></div>
            <div className="col-md-5 text-center">
              <img src={contactPic} alt="Contact" className="contact-img" />
            </div>

            <div className="col-md-5" id="contact-form">  
              <h4 className="mb-4">{t("contact.formTitle")}</h4>

              <form className="contact-form" onSubmit={handleSubmit}>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  placeholder={t("contact.form.name")}
                  value={form.name}
                  onChange={handleChange}
                />

                <PhoneInput
                  country="in"                 // ✅ default India
                  preferredCountries={["in", "ae"]} // ✅ India & UAE on top
                  enableSearch
                  countryCodeEditable={false}
                  value={form.phone}
                  onChange={(value) =>
                    setForm({ ...form, phone: value })
                  }
                  inputProps={{
                    name: "phone",
                    required: true
                  }}
                  containerClass="phone-container"
                  inputClass="form-control phone-input"
                  buttonClass="phone-flag-btn"
                />

                <input
                  type="email"
                  className="form-control"
                  name="email"
                  placeholder={t("contact.form.email")}
                  value={form.email}
                  onChange={handleChange}
                />

                <Select
                  options={enquiryOptions}
                  value={form.enquiryType}
                  onChange={handleSelectChange}
                  placeholder={t("contact.form.enquiryType")}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isSearchable={false}
                />

                {form.enquiryType?.value === "Careers" && (
                  <>
                    <input
                      type="file"
                      className="form-control"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                    />
                    <small className="cv-hint">
                      Only PDF / DOC / DOCX · Max 5MB
                    </small>
                  </>
                )}

                <textarea
                  className="form-control"
                  rows="4"
                  name="message"
                  placeholder={t("contact.form.message")}
                  value={form.message}
                  onChange={handleChange}
                />

                <button
                  className="btn c-btn mt-3"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting
                    ? t("contact.form.submitting")
                    : t("contact.form.submit")}
                </button>
              </form>
            </div>
          </div>

          {/* CONTACT INFO */}
          <div className="contact-info mt-4">
            <div className="col-md-6">
              <table className="contact-table">
                <tbody>
                  {/* UAE */}
                  <tr>
                    <td className="label">{t("contact.uaeOffice")}</td>
                    <td className="value">
                      {t("contact.uaePhoneLabel")}{" "}
                      <a href="tel:+971554405896">
                        {t("contact.uaePhone")}
                      </a>
                    </td>
                  </tr>

                  {/* INDIA */}
                  <tr>
                    <td className="label">{t("contact.indiaOffice")}</td>
                    <td className="value">
                      {t("contact.indiaPhoneLabel")}{" "}
                      <a href="tel:+917754925771">
                        {t("contact.indiaPhone1")}
                      </a>
                      <br />
                      {t("contact.indiaPhoneLabel")}{" "}
                      <a href="tel:+918090555551">
                        {t("contact.indiaPhone2")}
                      </a>
                      <br />
                      {t("contact.emailLabel")}{" "}
                      <a href="mailto:info@autoassure360.com">
                        {t("contact.email")}
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>



        </div>
      </section>

      <section
        className="careers"
        style={{ backgroundImage: `url(${careersBg})` }}
      >
        <div className="careers-overlay">
          <div className="container text-center">
            <h2>{t("contact.careers.title")}</h2>
            <p className="mt-3 mb-5">{t("contact.careers.text")}</p>

            <a href="#career" className="btn c-btn mt-5">
              {t("contact.careers.cta")}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
